import { CommonNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { useNodeViewCollaborationPresence } from '@/hooks/use-node-view-collaboration-presence';
import type { AttributeDef } from '@/lib/parseZyXML';
import { parseZyXML } from '@/lib/parseZyXML';
import { applyToolXmlUpdatesWithImageSync } from '@/lib/sync-tool-builder-images';
import { DEFAULT_EDITOR_OPTIONS } from '@/lib/utils';
import { Editor as MonacoEditor } from '@monaco-editor/react';
import { type ReactNodeViewProps } from '@tiptap/react';
import type { editor } from 'monaco-editor';
import { memo, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { setupMonacoEditorOnMount } from '../monaco-editor-utils';

const ZyToolNodeView: React.FC<ReactNodeViewProps> = ({ node, updateAttributes, selected, editor, decorations }) => {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const { id, toolXMLToLoad } = node.attrs;
    const [isMonacoEditorFocused, setIsMonacoEditorFocused] = useState(false);
    const { presenceUsers, presenceTracking } = useNodeViewCollaborationPresence(editor, id, 'zyTool', isMonacoEditorFocused);

    const parseZyXMLConfig = {
        rootTag: 'zyTool',
        xmlPropName: 'toolXMLToLoad',
        attributes: [
            { name: 'name', type: 'string' },
            { name: 'caption', type: 'string' },
            { name: 'height', type: 'number' },
            { name: 'width', type: 'number' },
            { name: 'challenge', type: 'boolean' },
            { name: 'parts', type: 'number' },
            { name: 'optional', type: 'boolean' },
            { name: 'image_ids', type: 'array', xmlName: 'image-ids' },
        ] as AttributeDef[],
        hasOptions: true,
        hasAltDescription: true,
        hasInstructions: true,
    };

    const handleUpdateToolXML = useDebounceCallback((newXML: string) => {
        const updates = parseZyXML(newXML, parseZyXMLConfig);
        if (!updates) {
            return;
        }
        applyToolXmlUpdatesWithImageSync(editor, String(id), updates, updateAttributes);
    }, 300);

    const handleEditorMount = (monacoEditor: editor.IStandaloneCodeEditor) => {
        setupMonacoEditorOnMount(
            monacoEditor,
            () => setIsMonacoEditorFocused(true),
            () => setIsMonacoEditorFocused(false),
        );
    };

    const mainContent = (
        <MonacoEditor
            defaultLanguage="xml"
            value={toolXMLToLoad}
            onChange={value => handleUpdateToolXML(value || '')}
            options={{ ...DEFAULT_EDITOR_OPTIONS, readOnly: isEditorReadonly }}
            theme="vs"
            onMount={handleEditorMount}
        />
    );

    const settingsPanel = <div className="bg-gray-100 px-4 py-4 dark:bg-gray-800 flex items-center justify-end border-b gap-x-2"></div>;

    return (
        <CommonNodeViewWrapper
            decorations={decorations}
            label="zyTool"
            selected={selected}
            presenceUsers={presenceUsers}
            presenceTracking={presenceTracking}
            contentClassName="pt-6"
            fullscreenContent={mainContent}
            settingsPanel={settingsPanel}
        >
            <div
                className="h-96"
                contentEditable={false}
            >
                {mainContent}
            </div>
        </CommonNodeViewWrapper>
    );
};

export default memo(ZyToolNodeView);
